import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type { components, paths } from "@breenova/api-client";

type Schemas = components["schemas"];

export type HealthRecordDto = Schemas["HealthRecordDto"];
export type HealthRecordKind = Schemas["HealthRecordKind"];
/** Taken from the query parameter — the segment filter is not a response schema. */
export type HealthFilter = NonNullable<
  NonNullable<paths["/api/v1/health-records"]["get"]["parameters"]["query"]>["Filter"]
>;
export type WeightLogDto = Schemas["WeightLogDto"];
export type HealthTimelineResponse = Schemas["HealthTimelineResponse"];
export type TimelineEntry = Schemas["TimelineEntry"];
export type MoltRecordDto = Schemas["MoltRecordDto"];

export const healthKeys = {
  all: ["health"] as const,
  records: (filter: HealthFilter, birdId?: string) =>
    ["health", "records", filter, birdId ?? null] as const,
  timeline: (birdId: string) => ["health", "timeline", birdId] as const,
  weights: (birdId: string) => ["health", "weights", birdId] as const,
};

function unwrap<T>(data: T | undefined, error: unknown, what: string): T {
  if (error || data === undefined) {
    throw new Error(`Could not load ${what}.`);
  }

  return data;
}

/** Pulls a readable message out of an RFC 9457 problem-details body. */
export function problemMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const problem = error as { detail?: string; title?: string; errors?: Record<string, string[]> };
    const first = problem.errors ? Object.values(problem.errors).flat()[0] : undefined;

    return first ?? problem.detail ?? problem.title ?? fallback;
  }

  return fallback;
}

export function useHealthRecords(filter: HealthFilter, birdId?: string) {
  return useQuery({
    queryKey: healthKeys.records(filter, birdId),
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/health-records", {
        params: {
          query: { Filter: filter, ...(birdId ? { BirdId: birdId } : {}), PageSize: 100 },
        },
      });

      return unwrap(data, error, "health records");
    },
    placeholderData: (previous) => previous,
  });
}

/**
 * The merged feed for one bird, plus its withdrawal warning and weight trend.
 *
 * One call rather than four: the ordering across sources is the value, and stitching it
 * client-side would mean paging four lists in lockstep.
 */
export function useHealthTimeline(birdId: string | undefined) {
  return useQuery({
    queryKey: healthKeys.timeline(birdId ?? ""),
    enabled: Boolean(birdId),
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/birds/{id}/health-timeline", {
        params: { path: { id: birdId! } },
      });

      return unwrap(data, error, "the health timeline") as HealthTimelineResponse;
    },
  });
}

export function useBirdWeights(birdId: string | undefined) {
  return useQuery({
    queryKey: healthKeys.weights(birdId ?? ""),
    enabled: Boolean(birdId),
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/birds/{id}/weights", {
        params: { path: { id: birdId! } },
      });

      return unwrap(data, error, "the weight history");
    },
  });
}

/**
 * Health writes invalidate the whole health tree and the bird itself.
 *
 * A treatment changes the withdrawal warning on the bird's detail screen, and a
 * cage-level one changes it for every bird in the cage — so precise invalidation would
 * mean knowing which birds were expanded, which is exactly what the server just decided.
 */
function useHealthMutation<TInput, TOutput>(mutationFn: (input: TInput) => Promise<TOutput>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: healthKeys.all });
      void queryClient.invalidateQueries({ queryKey: ["birds"] });
    },
  });
}

export interface CreateHealthRecordInput {
  birdId?: string | null;
  locationId?: string | null;
  kind: HealthRecordKind;
  title: string;
  details?: string | null;
  medication?: string | null;
  dosage?: string | null;
  startsOn?: string | null;
  endsOn?: string | null;
  withdrawalDays?: number | null;
  followUpOn?: string | null;
}

export function useCreateHealthRecord() {
  return useHealthMutation(async (input: CreateHealthRecordInput) => {
    const { data, error } = await api.POST("/api/v1/health-records", {
      body: {
        birdId: input.birdId ?? null,
        locationId: input.locationId ?? null,
        kind: input.kind,
        occurredOn: null,
        title: input.title,
        details: input.details ?? null,
        medication: input.medication ?? null,
        dosage: input.dosage ?? null,
        startsOn: input.startsOn ?? null,
        endsOn: input.endsOn ?? null,
        withdrawalDays: input.withdrawalDays ?? null,
        followUpOn: input.followUpOn ?? null,
      },
    });

    if (error) {
      throw new Error(problemMessage(error, "Could not save the record."));
    }

    return data;
  });
}

export function useResolveHealthRecord() {
  return useHealthMutation(async (input: { id: string; isResolved: boolean }) => {
    const { data, error } = await api.PUT("/api/v1/health-records/{id}", {
      params: { path: { id: input.id } },
      body: {
        id: input.id,
        title: null,
        details: null,
        medication: null,
        dosage: null,
        startsOn: null,
        endsOn: null,
        withdrawalDays: null,
        followUpOn: null,
        isResolved: input.isResolved,
      },
    });

    if (error) {
      throw new Error(problemMessage(error, "Could not update the record."));
    }

    return data;
  });
}

export function useAddWeight(birdId: string) {
  return useHealthMutation(async (input: { grams: number; notes?: string }) => {
    const { data, error } = await api.POST("/api/v1/birds/{id}/weights", {
      params: { path: { id: birdId } },
      body: {
        birdId,
        grams: input.grams,
        measuredOn: null,
        notes: input.notes ?? null,
      },
    });

    if (error) {
      throw new Error(problemMessage(error, "Could not record the weight."));
    }

    return data;
  });
}
